import { Icon, Typography, TypographyType } from '@/components/common';
import Icons from '@/const/icons';
import clsx, { ClassValue } from 'clsx';

export type SummaryTypes =
	| 'recovered'
	| 'active-cases'
	| 'resolved-cases'
	| 'on-hold-cases'
	| 'action-required-client'
	| 'action-required-lawyer'
	| 'closed-cases'
	| 'flagged-accounts'
	| 'deactivated-accounts'
	| 'review-required'
	| 'convertionRate'
	| 'new_accounts_created'
	| 'total_verified_phone'
	| 'total_accounts_created'
	| 'customer-satisfaction'
	| 'net-promoter-score'
	| 'total-surveys';

interface Props {
	type: SummaryTypes;
	quantity: string;
	customTitle?: string;
	className?: ClassValue;
	iconBgSize?: string;
	iconSize?: string;
	titleTypographyType?: TypographyType;
	classNameText?: ClassValue;
	classNameTextValue?: ClassValue;
	classNameContent?: ClassValue;
}

export const SummaryCard: React.FC<Props> = ({
	type,
	quantity,
	className,
	customTitle,
	iconBgSize,
	iconSize,
	titleTypographyType,
	classNameText,
	classNameTextValue,
	classNameContent,
}) => {
	let icon = '';
	switch (type) {
		case 'active-cases':
			icon = Icons.folderOpen;
			break;
		case 'resolved-cases':
			icon = Icons.folderCloud;
			break;
		case 'on-hold-cases':
			icon = Icons.folderCloudHold;
			break;
		case 'recovered':
			icon = Icons.moneyReceive;
			break;
		case 'action-required-client':
			icon = Icons.userOctagon;
			break;
		case 'action-required-lawyer':
			icon = Icons.userEdit;
			break;
		case 'closed-cases':
			icon = Icons.close;
			break;
		case 'deactivated-accounts':
		case 'flagged-accounts':
		case 'review-required':
			icon = Icons.flag;
			break;
		case 'convertionRate':
			icon = Icons.clipboard;
			break;
		case 'total_accounts_created':
			icon = Icons.profileGreen;
			break;
		case 'new_accounts_created':
			icon = Icons.profilePurple;
			break;
		case 'total_verified_phone':
			icon = Icons.phoneVerified;
			break;
		case 'customer-satisfaction':
			icon = Icons.smiley;
			break;
		case 'net-promoter-score':
			icon = Icons.like;
			break;
		case 'total-surveys':
			icon = Icons.files;
			break;
	}

	const getTitle = (): string => {
		return type.replaceAll('-', ' ');
	};

	const getIconBackground = (type: SummaryTypes): string => {
		switch (type) {
			case 'resolved-cases':
				return 'bg-purple-150';
			case 'on-hold-cases':
				return 'bg-yellow-base';
			case 'recovered':
				return 'bg-green-120';
			case 'active-cases':
			case 'convertionRate':
				return 'bg-[#2084E729]';
			case 'action-required-client':
				return 'bg-[#EB35CE29]';
			case 'action-required-lawyer':
				return 'bg-[#3957F429]';
			case 'closed-cases':
			case 'flagged-accounts':
				return 'bg-[#FF004329]';
			case 'deactivated-accounts':
				return 'bg-[#34343429]';
			case 'review-required':
				return 'bg-[#F6C64A29]';
			case 'total_accounts_created':
				return 'bg-[#40BB7129]';
			case 'new_accounts_created':
				return 'bg-[#8F20EF29]';
			case 'total_verified_phone':
				return 'bg-[#2D41F529]';
			case 'customer-satisfaction':
				return 'bg-[#2084E729]';
			case 'net-promoter-score':
				return 'bg-[#8039DF29]';
			case 'total-surveys':
				return 'bg-[#F4F4F4]';
			default:
				return '';
		}
	};

	return (
		<div
			className={clsx(
				'bg-white rounded-3xl py-[18px] 2xl:py-[5%] px-4',
				className
			)}
		>
			<div className={clsx('flex items-start gap-4 h-full', classNameContent)}>
				<div>
					<span
						className={clsx(
							iconBgSize || 'w-14 h-14',
							`rounded-full 5 flex justify-center items-center`,
							getIconBackground(type),
							{
								'!bg-opacity-15': type !== 'total-surveys',
							}
						)}
					>
						<Icon
							src={icon}
							className={clsx(iconSize || `!w-8 !h-8`, {
								'text-purple-150': type === 'resolved-cases',
								'text-yellow-base':
									type === 'on-hold-cases' || type === 'review-required',
								'text-green-120': type === 'recovered',
								'text-main-blue': type === 'active-cases',
								'text-[#EB35CE]': type === 'action-required-client',
								'text-[#2D41F5]': type === 'action-required-lawyer',
								'text-[#FF0043]':
									type === 'closed-cases' || type === 'flagged-accounts',
								'text-grey-90': type === 'deactivated-accounts',
							})}
						/>
					</span>
				</div>
				<div className="space-y-1">
					<Typography
						type={titleTypographyType || 'regular-14'}
						text={customTitle || getTitle()}
						className={clsx('opacity-60 text-left capitalize', classNameText)}
					/>
					<Typography
						type="semibold-24"
						text={quantity}
						className={clsx('text-left', classNameTextValue)}
					/>
				</div>
			</div>
		</div>
	);
};
